import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  if (!loading && user && user !== null) {
    return <Navigate to="/" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr("");
    try {
      await login(email, password);
      toast.success("Signed in");
      navigate("/");
    } catch (e) {
      const m = formatApiErrorDetail(e?.response?.data?.detail) || e.message;
      setErr(m);
      toast.error(m);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[44%] bg-ink text-white p-12 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "22px 22px",
          }}
        />
        <div className="relative">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-gold text-ink font-serif text-3xl font-semibold leading-none">3</span>
            <span className="font-serif text-4xl text-white leading-none">Realty</span>
          </div>
          <div className="text-sm text-slate-300 mt-3 tracking-wide">
            SARFAESI · ARC · NCLT — Maharashtra
          </div>
        </div>

        <div className="relative" />

        <div className="relative text-xs text-slate-400">
          Internal use only · 3 Realty CRM
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-paper app-grain">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-md bg-white border border-slate-200 rounded-md shadow-sm p-8"
          data-testid="login-form"
        >
          <div className="flex items-center gap-2.5 lg:hidden mb-5">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-gold text-ink font-serif text-xl font-semibold leading-none">3</span>
            <span className="font-serif text-2xl text-ink leading-none">Realty</span>
          </div>
          <div className="label-eyebrow">Sign in</div>
          <h1 className="font-display text-2xl mt-1 text-slate-900">
            Welcome back
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Use your team credentials to continue.
          </p>

          <div className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="text-xs">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9"
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            {err && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded" data-testid="login-error">
                {err}
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-slate-900 hover:bg-slate-800"
              disabled={submitting}
              data-testid="login-submit-button"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
